import { useCallback, useEffect, useState } from 'react';
import { TranscriptionService } from './transcription-service';

interface UseTranscriptionProps {
  audioUrl: string;
  messageUuid: string;
  currentTranscription?: string;
}

export function useTranscription({
  audioUrl,
  messageUuid,
  currentTranscription,
}: Readonly<UseTranscriptionProps>) {
  const TRY_AGAIN_MESSAGE =
    'Não foi possível transcrever o áudio, tente novamente em alguns instantes...';
  const DISABLED_TEMPORARY_MESSAGE =
    'Transcrição indisponível. Tente novamente mais tarde!';

  const MAX_TRY = 3;
  const MAX_TIME_TRY = 10000; // 10 segundos em milissegundos

  const [transcription, setTranscription] = useState(
    currentTranscription ?? ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [countTry, setCountTry] = useState(0);
  const [canTryAgain, setCanTryAgain] = useState(true);

  const handleTranscription = useCallback(async () => {
    setIsLoading(true);
    try {
      const file = (await TranscriptionService.getFile(
        audioUrl,
        messageUuid
      )) as unknown as File;

      const response = await TranscriptionService.transcription(
        file,
        messageUuid
      );
      if (response.transcription) {
        setTranscription(response.transcription);
        setError(false);
        setCanTryAgain(true);
      } else {
        setError(true);
      }
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
      setCountTry((prev) => prev + 1);
    }
  }, [audioUrl, messageUuid]);

  useEffect(() => {
    if (countTry >= MAX_TRY) {
      setCanTryAgain(false);
    }
  }, [countTry]);

  useEffect(() => {
    if (!canTryAgain) {
      setTimeout(() => {
        setCountTry(0);
        setCanTryAgain(true);
      }, MAX_TIME_TRY);
    }
  }, [canTryAgain]);

  /**
   * Retorna o texto para ser exibido no componente TranscriptionText
   * caso esteja carregando, retorna 'Transcrevendo...'
   * caso ocorra um erro e ainda puder tentar novamente, retorna 'Não foi possível transcrever o áudio, tente novamente...'
   * caso ocorra um erro e não puder tentar novamente, retorna 'Transcrição indisponível. Tente novamente mais tarde!'
   * caso tenha uma transcrição, retorna a transcrição
   * @returns string
   */
  function getText() {
    if (isLoading) return 'Transcrevendo...';
    if (error) return TRY_AGAIN_MESSAGE;
    if (error && !canTryAgain) return DISABLED_TEMPORARY_MESSAGE;
    if (transcription) return transcription;
    return '';
  }

  return {
    // states
    isLoading,
    error,
    canTryAgain,
    // functions
    handleTranscription,
    getText,
  };
}
